import React, { useState } from 'react';
import { ChevronRight, ChevronLeft } from 'lucide-react';

const ProductCard = ({ item, onReadMore, isAdmin }) => {
  // Handle multiple images or fall back to single image
  const images = item.imageUrls && item.imageUrls.length > 0
    ? item.imageUrls
    : item.imageUrl
      ? [item.imageUrl]
      : [];
  
  // Slider state
  const [currentIndex, setCurrentIndex] = useState(0);

  // Slide to next image
  const nextImage = (e) => {
    e.stopPropagation();
    if (images.length > 1) {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }
  };

  // Slide to previous image
  const prevImage = (e) => {
    e.stopPropagation();
    if (images.length > 1) {
      setCurrentIndex((prevIndex) => 
        prevIndex === 0 ? images.length - 1 : prevIndex - 1
      );
    }
  };

  // Check if product is a deal
  const isDeal = item.category === 'Deals' && item.dealQuantity;

  // Convert description to list style and limit to first 3 lines
  const descriptionList = item.description 
    ? item.description.split("\n").filter(line => line.trim() !== "")
    : [];

  // Determine if there are more than 3 items
  const hasMoreDescription = descriptionList.length > 3;

  return (
    <div 
      className={`relative bg-white rounded-lg shadow-md p-2 m-2 ${
        isDeal ? 'flex flex-col sm:flex-row max-w-3xl mx-auto w-full' : 'w-full sm:w-56 flex flex-col'
      } transition duration-200 ease-in-out transform hover:scale-105`}
      onClick={() => onReadMore(item)} 
      style={{
        boxShadow: isDeal 
          ? '0 6px 12px rgba(27, 197, 36, 0.8)' 
          : '0 4px 6px rgba(37, 99, 235, 0.1)',
        maxWidth: isDeal ? '800px' : '100%',
        width: isDeal ? '100%' : 'auto',
        height: isDeal ? 'auto' : 'auto'
      }}
    >
      {/* Image Section */}
      <div 
        className={`relative ${
          isDeal ? 'w-full sm:w-1/3 h-64 sm:h-72' : 'h-52 sm:h-56 mb-2'
        } flex items-center justify-center bg-gray-100 rounded-md overflow-hidden`}
      >
        {images.length > 0 ? (
          <img 
            src={images[currentIndex]}
            alt={item.name}
            className={`${
              isDeal ? 'max-h-full max-w-full object-contain' : 'max-h-full max-w-full object-contain rounded-md'
            }`}
            style={{
              objectFit: 'contain',
              objectPosition: 'center',
            }}
          />
        ) : (
          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
            <span className="text-gray-500 text-xs">No image available</span>
          </div>
        )}

        {/* Image Slider Arrows */}
        {images.length > 1 && (
          <>
            <button 
              className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full p-1"
              onClick={prevImage}
            >
              <ChevronLeft size={20} />
            </button>
            <button 
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full p-1"
              onClick={nextImage}
            >
              <ChevronRight size={20} />
            </button>
          </>
        )}
      </div>

      {/* Product Details */}
      <div className={`p-3 sm:p-4 flex flex-col justify-between flex-grow`}>
        {/* Deal Badge */}
        {isDeal && (
          <div className="absolute top-2 left-2 bg-green-600 text-white px-2 py-1 rounded text-xs font-semibold">
            Deal
          </div>
        )}

        {/* Product Title */}
        <h3 
          className="font-bold text-base sm:text-lg mb-2 whitespace-pre-line"
        >
          {Array.isArray(item.name) ? (
            item.name.map((dealItem, index) => (
              <span key={index} className="block">{dealItem}</span>
            ))
          ) : (
            item.name
          )}
        </h3>

        {/* Full Description with line breaks */}
{item.description && (
  <div className="text-xs sm:text-sm text-gray-700 whitespace-pre-line mb-2 flex-grow">
    {item.description}
  </div>
)}

        {/* Floored Price, Bulk Pricing, and Button */}
        <div className="mt-auto space-y-2">
          <div className="text-sm sm:text-lg font-bold text-green-600">
            {isDeal ? (
              <span>{item.dealQuantity} for ${item.dealPrice}</span>
            ) : (
              <span>${typeof item.price === 'number' ? item.price.toFixed(2) : parseFloat(item.price || 0).toFixed(2)}</span>
            )}
          </div>

          {/* Bulk Pricing (Only for Regular Products) */}
          {!isDeal && item.hasBulkPricing && item.bulkOptions?.length > 0 && (
            <div className="text-green-600 text-xs sm:text-sm space-y-1">
              {item.bulkOptions.map((option, index) => (
                <div key={index} className="flex justify-between">
                  <span>{option.quantity}x</span>
                  <span>${parseFloat(option.price).toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}

          {/* Action Button - Floored */}
          <div className="flex justify-end mt-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onReadMore(item);
              }}
              className="text-blue-500 hover:text-blue-700 text-xs sm:text-sm font-semibold flex items-center transition duration-150 ease-in-out"
            >
              {isAdmin ? "Edit" : "View Details"}
              <ChevronRight size={16} className="ml-1" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
